import { Camera, ChevronRight, Sparkles } from 'lucide-react';

import type { ScanAvailability } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

interface ReceiptActionRowsProps {
  availability: ScanAvailability;
  collapsed: boolean;
  /** Itemizing is allowed for this caller in this group. */
  itemsAllowed?: boolean;
  onScan: () => void;
  onAttach: () => void;
}

/** The quota, exhausted or offline line; none for a caller nobody counts. */
export function ScanAvailabilityLine({
  availability,
}: {
  availability: ScanAvailability;
}) {
  const { t } = useTranslation();
  if (availability.kind === 'exhausted' && availability.fairUse) {
    // A new tab, so the half-filled expense form survives.
    return (
      <a
        href="/contact"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="scan-fair-use-line"
        className="block text-sm text-primary-600 underline hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
      >
        {t('expenses:scan.fairUseReached')}
      </a>
    );
  }
  const text =
    availability.kind === 'counted'
      ? t('expenses:scan.scansUsed', {
          used: availability.allowance - availability.left,
          allowance: availability.allowance,
        })
      : availability.kind === 'exhausted'
        ? t('expenses:scan.scansNoneLeft')
        : availability.kind === 'offline'
          ? t('expenses:scan.offlineHint')
          : null;
  if (text === null) return null;
  return (
    <span className="block text-sm text-gray-700 dark:text-gray-300">
      {text}
    </span>
  );
}

/** Scan with AI and Attach, as two bordered rows or, once collapsed, a compact pair. */
export function ReceiptActionRows({
  availability,
  collapsed,
  itemsAllowed = false,
  onScan,
  onAttach,
}: ReceiptActionRowsProps) {
  const { t } = useTranslation();
  const offline = availability.kind === 'offline';
  const exhausted = availability.kind === 'exhausted';
  // The fair-use line is a link, so it can't sit inside the Scan button.
  const fairUse = availability.kind === 'exhausted' && !!availability.fairUse;
  const scanDisabled = offline || exhausted;
  const dim = scanDisabled ? 'opacity-50' : '';

  if (collapsed) {
    return (
      <div className="space-y-2">
        <div
          data-testid="receipt-compact-pair"
          className="flex flex-wrap gap-2"
        >
          <button
            type="button"
            data-testid="receipt-scan-row"
            onClick={onScan}
            disabled={scanDisabled}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 disabled:cursor-not-allowed"
          >
            <Sparkles
              data-testid="receipt-scan-glyph"
              aria-hidden="true"
              className={`h-5 w-5 shrink-0 text-ai-accent dark:text-ai-accent-dark ${dim}`}
            />
            <span data-testid="receipt-scan-title" className={dim}>
              {t('expenses:scan.scanWithAi')}
            </span>
          </button>
          <button
            type="button"
            data-testid="receipt-attach-row"
            onClick={onAttach}
            disabled={offline}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Camera
              aria-hidden="true"
              className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
            />
            <span>{t('expenses:scan.attach')}</span>
          </button>
        </div>
        {/* Pro never sees the counted-scan confirm, so on the edit form this
            line is its only word on where the photo goes. Offline and
            exhausted are explained by the availability line instead. */}
        {scanDisabled ? null : (
          <p
            data-testid="receipt-scan-hint"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {itemsAllowed
              ? t('expenses:scan.scanWithAiHintItems')
              : t('expenses:scan.scanWithAiHint')}
          </p>
        )}
        <ScanAvailabilityLine availability={availability} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        data-testid="receipt-scan-row"
        onClick={onScan}
        disabled={scanDisabled}
        className="flex w-full items-start gap-3 rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-3 text-left disabled:cursor-not-allowed"
      >
        <Sparkles
          data-testid="receipt-scan-glyph"
          aria-hidden="true"
          className={`mt-0.5 h-5 w-5 shrink-0 text-ai-accent dark:text-ai-accent-dark ${dim}`}
        />
        <span className="min-w-0 flex-1 space-y-1">
          <span
            data-testid="receipt-scan-title"
            className={`block text-base font-semibold text-gray-900 dark:text-gray-100 ${dim}`}
          >
            {t('expenses:scan.scanWithAi')}
          </span>
          <span className="block text-sm text-gray-500 dark:text-gray-400">
            {itemsAllowed
              ? t('expenses:scan.scanWithAiHintItems')
              : t('expenses:scan.scanWithAiHint')}
          </span>
          {offline || fairUse ? null : (
            <ScanAvailabilityLine availability={availability} />
          )}
        </span>
        <ChevronRight
          data-testid="receipt-scan-chevron"
          aria-hidden="true"
          className="h-5 w-5 shrink-0 self-center text-gray-400 dark:text-gray-500"
        />
      </button>
      {fairUse ? <ScanAvailabilityLine availability={availability} /> : null}
      <button
        type="button"
        data-testid="receipt-attach-row"
        onClick={onAttach}
        disabled={offline}
        className="flex w-full items-start gap-3 rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Camera
          aria-hidden="true"
          className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
        />
        <span className="min-w-0 flex-1 space-y-1">
          <span className="block text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('expenses:scan.attach')}
          </span>
          <span className="block text-sm text-gray-500 dark:text-gray-400">
            {t('expenses:scan.attachHint')}
          </span>
        </span>
        <ChevronRight
          data-testid="receipt-attach-chevron"
          aria-hidden="true"
          className="h-5 w-5 shrink-0 self-center text-gray-400 dark:text-gray-500"
        />
      </button>
      {offline ? <ScanAvailabilityLine availability={availability} /> : null}
    </div>
  );
}
